import { useState } from 'react';
import { Info, Loader2, X } from 'lucide-react';
import {
  FLIPKART_PLACEMENT_FIELDS,
  FLIPKART_PLACEMENT_MODE_LABELS,
  emptyFlipkartPlacementForm,
  parseFlipkartPlacementForm,
  type FlipkartPlacementAdjustment,
  type FlipkartPlacementMode,
} from '@/components/flipkart/flipkartPlacementBidding';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { cn } from '@/utils/cn';

export function FlipkartPlacementBiddingModal({
  selectionCount,
  pending,
  onCancel,
  onConfirm,
}: {
  selectionCount: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (adjustments: FlipkartPlacementAdjustment[]) => void;
}) {
  const [form, setForm] = useState(emptyFlipkartPlacementForm);
  const parsed = parseFlipkartPlacementForm(form);
  const noun = selectionCount === 1 ? 'Ad Group' : 'Ad Groups';
  const canApply = !pending && !parsed.error && parsed.adjustments.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="relative flex w-full max-w-2xl flex-col rounded-xl bg-surface p-5 shadow-xl">
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          onClick={onCancel}
          disabled={pending}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h3 className="pr-8 text-base font-semibold text-foreground">
          Change Placement Bidding for {selectionCount} {noun}
        </h3>

        <div className="mt-4 flex gap-2 rounded-lg border border-warning/40 bg-warning-tint px-3 py-2.5 text-body-sm text-warning-ink">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            These actions apply to PLA campaigns only. PCA (Product Contextual Ads) campaigns are
            not supported — any PCA entities in your selection will be skipped.
          </p>
        </div>

        <p className="mt-4 text-body-sm text-foreground-muted">
          Adjust placement bids for selected ad groups. Leave value empty to skip a placement.
        </p>

        <p className="mt-3 rounded-lg border border-brand/20 bg-brand-subtle px-3 py-2 text-body-sm text-brand-ink">
          Minimum bid per placement will be enforced automatically based on pricing info.
        </p>

        <div className="mt-4 space-y-3">
          {FLIPKART_PLACEMENT_FIELDS.map((field) => (
            <div
              key={field.id}
              className="grid grid-cols-[minmax(0,1fr)_11.5rem_6.5rem] items-center gap-3"
            >
              <label
                htmlFor={`flipkart-placement-${field.id}-mode`}
                className="text-body-md font-medium text-foreground"
              >
                {field.label}
              </label>
              <Select
                id={`flipkart-placement-${field.id}-mode`}
                className="h-9 w-full"
                value={form[field.id].mode}
                disabled={pending}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    [field.id]: {
                      ...prev[field.id],
                      mode: event.target.value as FlipkartPlacementMode | '',
                    },
                  }))
                }
                aria-label={`${field.label} adjustment`}
              >
                <option value=""> </option>
                {(Object.keys(FLIPKART_PLACEMENT_MODE_LABELS) as FlipkartPlacementMode[]).map(
                  (option) => (
                    <option key={option} value={option}>
                      {FLIPKART_PLACEMENT_MODE_LABELS[option]}
                    </option>
                  )
                )}
              </Select>
              <Input
                id={`flipkart-placement-${field.id}-value`}
                className="h-9 w-full"
                type="number"
                min="0"
                step="0.01"
                value={form[field.id].value}
                disabled={pending}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    [field.id]: { ...prev[field.id], value: event.target.value },
                  }))
                }
                aria-label={`${field.label} value`}
              />
            </div>
          ))}
        </div>
        {parsed.error ? <p className="mt-3 text-body-sm text-error">{parsed.error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            className={cn(pending && 'pointer-events-none')}
            onClick={() => {
              if (!canApply) return;
              onConfirm(parsed.adjustments);
            }}
            disabled={!canApply}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
